"use strict";
/**
 * @license
 * Copyright 2016 Google Inc.
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *      http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { debounce } from "lodash-es";
import { TrackableValue } from "#src/trackable_value.js";
import { animationFrameDebounce } from "#src/util/animation_frame_debounce.js";
import { RefCounted } from "#src/util/disposable.js";
import { FramerateMonitor } from "#src/util/framerate.js";
import { parseFixedLengthArray, verifyFloat01 } from "#src/util/json.js";
import { NullarySignal } from "#src/util/signal.js";
import { initializeWebGL } from "#src/webgl/context.js";
const DELAY_AFTER_CONTINUOUS_CAMERA_MOTION_MS = 300;
export class RenderViewport {
  constructor() {
    // Width of visible portion of panel in canvas pixels.
    this.width = 0;
    // Height of visible portion of panel in canvas pixels.
    this.height = 0;
    // Width in canvas pixels, including portions outside of the canvas (i.e. outside the "viewport"
    // window).
    this.logicalWidth = 0;
    // Height in canvas pixels, including portions outside of the canvas (i.e. outside the "viewport"
    // window).
    this.logicalHeight = 0;
    // Left edge of visible region within full (logical) panel, as fraction in [0, 1].
    this.visibleLeftFraction = 0;
    // Top edge of visible region within full (logical) panel, as fraction in [0, 1].
    this.visibleTopFraction = 0;
    // Fraction of logical width that is visible, equal to `widthInCanvasPixels / logicalWidth`.
    this.visibleWidthFraction = 0;
    // Fraction of logical height that is visible, equal to `heightInCanvasPixels / logicalHeight`.
    this.visibleHeightFraction = 0;
  }
}
export function applyRenderViewportToProjectionMatrix(viewport, projectionMatrix) {
  const xScale = 1 / viewport.visibleWidthFraction;
  const yScale = 1 / viewport.visibleHeightFraction;
  const xOffset = -1 - (-1 + 2 * viewport.visibleLeftFraction) * xScale;
  let yOffset = -1 - (-1 + 2 * viewport.visibleTopFraction) * yScale;
  yOffset = -yOffset;
  projectionMatrix[0] = projectionMatrix[0] * xScale + projectionMatrix[3] * xOffset;
  projectionMatrix[4] = projectionMatrix[4] * xScale + projectionMatrix[7] * xOffset;
  projectionMatrix[8] = projectionMatrix[8] * xScale + projectionMatrix[11] * xOffset;
  projectionMatrix[12] = projectionMatrix[12] * xScale + projectionMatrix[15] * xOffset;
  projectionMatrix[1] = projectionMatrix[1] * yScale + projectionMatrix[3] * yOffset;
  projectionMatrix[5] = projectionMatrix[5] * yScale + projectionMatrix[7] * yOffset;
  projectionMatrix[9] = projectionMatrix[9] * yScale + projectionMatrix[11] * yOffset;
  projectionMatrix[13] = projectionMatrix[13] * yScale + projectionMatrix[15] * yOffset;
}
export function renderViewportsEqual(a, b) {
  return a.width === b.width && a.height === b.height && a.logicalWidth === b.logicalWidth && a.logicalHeight === b.logicalHeight && a.visibleLeftFraction === b.visibleLeftFraction && a.visibleTopFraction === b.visibleTopFraction;
}
export class RenderedPanel extends RefCounted {
  constructor(context, element, visibility) {
    super();
    this.context = context;
    this.element = element;
    this.visibility = visibility;
    // Generation used to check whether the following bounds-related fields are up to date.
    this.boundsGeneration = -1;
    // Offset of visible portion of panel in canvas pixels from left side of canvas.
    this.canvasRelativeClippedLeft = 0;
    // Offset of visible portion of panel in canvas pixels from top of canvas.
    this.canvasRelativeClippedTop = 0;
    this.canvasRelativeLogicalLeft = 0;
    this.canvasRelativeLogicalTop = 0;
    this.renderViewport = new RenderViewport();
    this.monitorState = {};
    this.gl = context.gl;
    context.addPanel(this);
  }
  scheduleRedraw() {
    if (this.visible) {
      this.context.scheduleRedraw();
    }
  }
  ensureBoundsUpdated() {
    const { context } = this;
    context.ensureBoundsUpdated();
    const { boundsGeneration } = context;
    if (boundsGeneration === this.boundsGeneration) return;
    this.boundsGeneration = boundsGeneration;
    const { element } = this;
    const clientRect = element.getBoundingClientRect();
    context.ensureMonitorPanel(element, this.monitorState, clientRect);
    const root = context.container;
    const canvasRect = context.canvasRect;
    const { canvas } = context;
    const { width: canvasPixelWidth, height: canvasPixelHeight } = canvas;
    const screenToCanvasPixelScaleX = canvasPixelWidth / canvasRect.width;
    const screenToCanvasPixelScaleY = canvasPixelHeight / canvasRect.height;
    const canvasLeft = canvasRect.left;
    const canvasTop = canvasRect.top;
    const logicalLeft = this.canvasRelativeLogicalLeft = Math.round(
      (clientRect.left - canvasLeft) * screenToCanvasPixelScaleX + element.clientLeft
    );
    const logicalTop = this.canvasRelativeLogicalTop = Math.round(
      (clientRect.top - canvasTop) * screenToCanvasPixelScaleY + element.clientTop
    );
    const logicalWidth = element.clientWidth;
    const logicalHeight = element.clientHeight;
    const logicalRight = logicalLeft + logicalWidth;
    const logicalBottom = logicalTop + logicalHeight;
    let clippedTop = logicalTop;
    let clippedLeft = logicalLeft;
    let clippedRight = logicalRight;
    let clippedBottom = logicalBottom;
    for (let parent = element.parentElement; parent !== null && parent !== root; parent = parent.parentElement) {
      const rect = parent.getBoundingClientRect();
      if (rect.x === 0 && rect.y === 0 && rect.width === 0 && rect.height === 0) {
        continue;
      }
      clippedLeft = Math.max(
        clippedLeft,
        (rect.left - canvasLeft) * screenToCanvasPixelScaleX
      );
      clippedTop = Math.max(
        clippedTop,
        (rect.top - canvasTop) * screenToCanvasPixelScaleY
      );
      clippedRight = Math.min(
        clippedRight,
        (rect.right - canvasLeft) * screenToCanvasPixelScaleX
      );
      clippedBottom = Math.min(
        clippedBottom,
        (rect.bottom - canvasTop) * screenToCanvasPixelScaleY
      );
    }
    clippedTop = this.canvasRelativeClippedTop = Math.round(
      Math.max(clippedTop, 0)
    );
    clippedLeft = this.canvasRelativeClippedLeft = Math.round(
      Math.max(clippedLeft, 0)
    );
    clippedRight = Math.round(Math.min(clippedRight, canvasPixelWidth));
    clippedBottom = Math.round(Math.min(clippedBottom, canvasPixelHeight));
    const viewport = this.renderViewport;
    const clippedWidth = viewport.width = Math.max(
      0,
      clippedRight - clippedLeft
    );
    const clippedHeight = viewport.height = Math.max(
      0,
      clippedBottom - clippedTop
    );
    viewport.logicalWidth = logicalWidth;
    viewport.logicalHeight = logicalHeight;
    viewport.visibleLeftFraction = (clippedLeft - logicalLeft) / logicalWidth;
    viewport.visibleTopFraction = (clippedTop - logicalTop) / logicalHeight;
    viewport.visibleWidthFraction = clippedWidth / logicalWidth;
    viewport.visibleHeightFraction = clippedHeight / logicalHeight;
  }
  // Sets the viewport to the clipped viewport.  Any drawing must take
  // `visible{Left,Top,Width,Height}Fraction` into account.  setGLClippedViewport() {
  setGLClippedViewport() {
    const {
      gl,
      canvasRelativeClippedTop,
      canvasRelativeClippedLeft,
      renderViewport: { width, height }
    } = this;
    const bottom = canvasRelativeClippedTop + height;
    gl.enable(WebGL2RenderingContext.SCISSOR_TEST);
    const glBottom = this.context.canvas.height - bottom;
    gl.viewport(canvasRelativeClippedLeft, glBottom, width, height);
    gl.scissor(canvasRelativeClippedLeft, glBottom, width, height);
  }
  // Sets the viewport to the logical viewport, using the scissor test to constrain drawing to the
  // clipped viewport.  Drawing does not need to take `visible{Left,Top,Width,Height}Fraction` into
  // account.
  setGLLogicalViewport() {
    const {
      gl,
      renderViewport: { width, height, logicalWidth, logicalHeight }
    } = this;
    const canvasHeight = this.context.canvas.height;
    gl.enable(WebGL2RenderingContext.SCISSOR_TEST);
    gl.viewport(
      this.canvasRelativeLogicalLeft,
      canvasHeight - (this.canvasRelativeLogicalTop + logicalHeight),
      logicalWidth,
      logicalHeight
    );
    gl.scissor(
      this.canvasRelativeClippedLeft,
      canvasHeight - (this.canvasRelativeClippedTop + height),
      width,
      height
    );
  }
  disposed() {
    this.context.unmonitorPanel(this.element, this.monitorState);
    this.context.removePanel(this);
    super.disposed();
  }
  get visible() {
    return this.visibility.visible;
  }
  getDepthArray() {
    return void 0;
  }
  get shouldDraw() {
    if (!this.visible) return false;
    const { element } = this;
    if (element.clientWidth === 0 || element.clientHeight === 0 || element.offsetWidth === 0 || element.offsetHeight === 0) {
      return false;
    }
    return true;
  }
  // Returns a number that determine the order in which panels are drawn. This is used by CdfPanel
  // to ensure it is drawn after other panels that update the histogram.
  //
  // A higher number -> later draw.
  get drawOrder() {
    return 0;
  }
}
export class IndirectRenderedPanel extends RenderedPanel {
  constructor(context, element, visibility) {
    super(context, element, visibility);
    this.canvas = document.createElement("canvas");
    this.canvasRenderingContext = this.canvas.getContext("2d");
    const { canvas } = this;
    element.appendChild(canvas);
    element.style.position = "relative";
    canvas.style.position = "absolute";
    canvas.style.left = "0";
    canvas.style.right = "0";
    canvas.style.top = "0";
    canvas.style.bottom = "0";
  }
  draw() {
    this.drawIndirect();
    const { renderViewport, canvas } = this;
    const { logicalWidth, logicalHeight } = renderViewport;
    canvas.width = logicalWidth;
    canvas.height = logicalHeight;
    const { canvasRenderingContext } = this;
    canvasRenderingContext?.drawImage(
      this.context.canvas,
      this.canvasRelativeLogicalLeft,
      this.canvasRelativeLogicalTop,
      logicalWidth,
      logicalHeight,
      0,
      0,
      logicalWidth,
      logicalHeight
    );
  }
}
export class TrackableWindowedViewport extends TrackableValue {
  constructor() {
    super(
      Float64Array.of(0, 0, 1, 1),
      (obj) => parseFixedLengthArray(new Float64Array(4), obj, verifyFloat01)
    );
  }
  toJSON() {
    const { value } = this;
    const [left, top, width, height] = value;
    if (left === 0 && top === 0 && width === 1 && height === 1)
      return void 0;
    return Array.from(value);
  }
}
export class DisplayContext extends RefCounted {
  constructor(container) {
    super();
    this.container = container;
    this.canvas = document.createElement("canvas");
    this.updateStarted = new NullarySignal();
    this.updateFinished = new NullarySignal();
    this.continuousCameraMotionStarted = new NullarySignal();
    this.continuousCameraMotionFinished = new NullarySignal();
    this.changed = this.updateFinished;
    this.panels = /* @__PURE__ */ new Set();
    this.resizeGeneration = 0;
    this.boundsGeneration = -1;
    this.framerateMonitor = new FramerateMonitor();
    this.continuousCameraMotionInProgress = false;
    // Panels ordered by `drawOrder`.  If length is 0, needs to be recomputed.
    this.orderedPanels = [];
    /**
     * Unique number of the next frame.  Incremented once each time a frame is drawn.
     */
    this.frameNumber = 0;
    this.resizeCallback = () => {
      ++this.resizeGeneration;
      this.scheduleRedraw();
    };
    this.resizeObserver = new ResizeObserver(this.resizeCallback);
    this.debouncedEndContinuousCameraMotion = this.registerCancellable(
      debounce(() => {
        this.continuousCameraMotionInProgress = false;
        this.continuousCameraMotionFinished.dispatch();
      }, DELAY_AFTER_CONTINUOUS_CAMERA_MOTION_MS)
    );
    this.scheduleRedraw = this.registerCancellable(
      animationFrameDebounce(() => this.draw())
    );
    const { canvas, resizeObserver } = this;
    container.style.position = "relative";
    canvas.style.position = "absolute";
    canvas.style.top = "0px";
    canvas.style.left = "0px";
    canvas.style.width = "100%";
    canvas.style.height = "100%";
    canvas.style.zIndex = "0";
    resizeObserver.observe(canvas);
    container.appendChild(canvas);
    this.registerEventListener(
      canvas,
      "webglcontextlost",
      (event) => {
        console.log(`Lost WebGL context: ${event.statusMessage}`);
        event.preventDefault();
      }
    );
    this.registerEventListener(canvas, "webglcontextrestored", () => {
      console.log("WebGL context restored");
      window.location.reload();
    });
    this.gl = initializeWebGL(canvas);
  }
  ensureMonitorPanel(element, state, elementClientRect) {
    if (!state.addedToResizeObserver) {
      this.resizeObserver.observe(element);
      state.addedToResizeObserver = true;
    }
    const rootRect = this.rootRect;
    const marginTop = rootRect.top - elementClientRect.top;
    const marginLeft = rootRect.left - elementClientRect.left;
    const marginRight = elementClientRect.right - rootRect.right;
    const marginBottom = elementClientRect.bottom - rootRect.bottom;
    const margin = `${marginTop}px ${marginRight}px ${marginBottom}px ${marginLeft}px`;
    if (state.intersectionObserverMargin !== margin) {
      state.intersectionObserverMargin = margin;
      state.intersectionObserver?.disconnect();
      const intersectionObserver = state.intersectionObserver = new IntersectionObserver(this.resizeCallback, {
        root: this.container,
        rootMargin: margin,
        threshold: [0.93, 0.94, 0.95, 0.96, 0.97, 0.98, 0.99, 1]
      });
      intersectionObserver.observe(element);
    }
  }
  unmonitorPanel(element, state) {
    if (state.addedToResizeObserver) {
      this.resizeObserver.unobserve(element);
    }
    state.intersectionObserver?.disconnect();
  }
  flagContinuousCameraMotion() {
    if (!this.continuousCameraMotionInProgress) {
      this.continuousCameraMotionStarted.dispatch();
    }
    this.continuousCameraMotionInProgress = true;
    this.debouncedEndContinuousCameraMotion();
  }
  get isContinuousCameraMotionInProgress() {
    return this.continuousCameraMotionInProgress;
  }
  applyWindowedViewportToElement(element, value) {
    const [left, top, width, height] = value;
    const totalWidth = 1 / width;
    const totalHeight = 1 / height;
    element.style.position = "absolute";
    element.style.top = `${-totalHeight * top * 100}%`;
    element.style.left = `${-totalWidth * left * 100}%`;
    element.style.width = `${totalWidth * 100}%`;
    element.style.height = `${totalHeight * 100}%`;
    ++this.resizeGeneration;
    this.scheduleRedraw();
  }
  isReady() {
    for (const panel of this.panels) {
      if (!panel.visible) {
        continue;
      }
      if (!panel.isReady()) {
        return false;
      }
    }
    return true;
  }
  /**
   * Returns a child element that overlays the canvas.
   */
  makeCanvasOverlayElement() {
    const element = document.createElement("div");
    element.style.position = "absolute";
    element.style.top = "0px";
    element.style.left = "0px";
    element.style.width = "100%";
    element.style.height = "100%";
    element.style.zIndex = "2";
    this.container.appendChild(element);
    return element;
  }
  disposed() {
    this.orderedPanels.length = 0;
    this.resizeObserver.disconnect();
  }
  addPanel(panel) {
    this.panels.add(panel);
    this.orderedPanels.length = 0;
    ++this.resizeGeneration;
    this.scheduleRedraw();
  }
  removePanel(panel) {
    this.panels.delete(panel);
    this.orderedPanels.length = 0;
    ++this.resizeGeneration;
    this.scheduleRedraw();
  }
  ensureBoundsUpdated() {
    const { resizeGeneration } = this;
    if (this.boundsGeneration === resizeGeneration) return;
    const { canvas } = this;
    canvas.width = canvas.offsetWidth;
    canvas.height = canvas.offsetHeight;
    this.canvasRect = canvas.getBoundingClientRect();
    this.rootRect = this.container.getBoundingClientRect();
    this.boundsGeneration = resizeGeneration;
  }
  draw() {
    ++this.frameNumber;
    this.updateStarted.dispatch();
    const gl = this.gl;
    const ext = this.framerateMonitor.getTimingExtension(gl);
    const query = this.framerateMonitor.startFrameTimeQuery(gl, ext);
    this.ensureBoundsUpdated();
    this.gl.clearColor(0, 0, 0, 0);
    gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);
    const { orderedPanels, panels } = this;
    if (orderedPanels.length !== panels.size) {
      orderedPanels.push(...panels);
      orderedPanels.sort((a, b) => a.drawOrder - b.drawOrder);
    }
    for (const panel of orderedPanels) {
      if (!panel.shouldDraw) continue;
      panel.ensureBoundsUpdated();
      const { renderViewport } = panel;
      if (renderViewport.width === 0 || renderViewport.height === 0) continue;
      panel.draw();
    }
    gl.disable(gl.SCISSOR_TEST);
    this.gl.clearColor(1, 1, 1, 1);
    this.gl.colorMask(false, false, false, true);
    gl.clear(gl.COLOR_BUFFER_BIT);
    this.gl.colorMask(true, true, true, true);
    this.updateFinished.dispatch();
    this.framerateMonitor.endFrameTimeQuery(gl, ext, query);
    this.framerateMonitor.grabAnyFinishedQueryResults(gl);
  }
  getDepthArray() {
    const { width, height } = this.canvas;
    const depthArray = new Float32Array(width * height);
    for (const panel of this.panels) {
      if (!panel.shouldDraw) continue;
      const panelDepthArray = panel.getDepthArray();
      if (panelDepthArray === void 0) continue;
      const {
        canvasRelativeClippedTop,
        canvasRelativeClippedLeft,
        renderViewport: { width: width2, height: height2 }
      } = panel;
      for (let y = 0; y < height2; ++y) {
        const panelDepthArrayOffset = (height2 - 1 - y) * width2;
        depthArray.set(
          panelDepthArray.subarray(
            panelDepthArrayOffset,
            panelDepthArrayOffset + width2
          ),
          (canvasRelativeClippedTop + y) * width2 + canvasRelativeClippedLeft
        );
      }
    }
    return depthArray;
  }
  getLastFrameTimesInMs(numberOfFrames = 10) {
    return this.framerateMonitor.getLastFrameTimesInMs(numberOfFrames);
  }
}
